// Pure mapping helpers for the redesigned import-progress notification
// (AHA2-171, "Import Flow — Functional Spec"). Kept framework-free so the same
// state machine can drive both the notification component and the import
// trigger button's tooltip/spinner, and so the logic is unit-testable without a
// mounted component.
//
// The six named states come straight from §3 of the spec. Crucially the state
// is derived from the raw signals we actually receive from the backend job
// (`phase`, `slides.created`, `slides.total`) — NOT from a status string the
// backend sends, because the backend has no notion of "almost done" or the
// cancel/connection-loss UI states; those are a frontend concern.

export const IMPORT_UI_STATE = {
  UPLOADING: 'uploading',
  CONVERTING: 'converting',
  ALMOST_DONE: 'almost_done',
  COMPLETE: 'complete',
  CANCELED: 'canceled',
  CONNECTION_LOST: 'connection_lost',
  // AHA2-171 r20: a genuine import failure or poll-timeout (the job reported
  // 'failed', or waitForImportJob threw). Distinct from CANCELED (user intent)
  // and COMPLETE (success) so the notification shows a real error card instead
  // of the green "Import complete" a finished-with-≥1-slide end used to fake.
  ERROR: 'error',
}

// The backend job's `result.phase` values. 'converting' is the file→images
// conversion the backend runs before any slide exists — from the user's point
// of view that is still the "Uploading file…" phase (§3 note: there is
// deliberately no separate "analyzing" state). 'importing' is when slides are
// being created (we have a live created/total count). 'captioning' runs after
// the slides already exist, so it reads as "Almost done".
export const BACKEND_PHASE = {
  CONVERTING: 'converting',
  IMPORTING: 'importing',
  CAPTIONING: 'captioning',
}

// Above this fraction of created/total we switch "Importing slides…" for
// "Almost done…" and hide the Cancel button (§3 row 3: not worth offering
// cancel this close to completion).
export const ALMOST_DONE_FRACTION = 0.9

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

/**
 * AHA2-171 r16: the ONE real, bounded, monotonic progress signal the backend
 * emits. `slides.created/total` has no total until captioning starts (verified
 * r11), so the bar used to be driven by an unreliable fake time-ramp for the
 * whole upload/convert/import stretch. `captions.done/total` only exists
 * during the CAPTIONING phase, but it is the real thing — a known total and a
 * count that only goes up. `done + failed` counts as the numerator ("settled")
 * so a permanently-failed caption can't stall the bar just short of 100.
 * Returns null when there is no caption signal yet (upload / converting /
 * importing, before captioning starts) — callers fall back to the
 * indeterminate lead-in.
 */
export const computeCaptionFraction = (done = 0, failed = 0, total = 0) => (
  total > 0 ? clamp((Number(done) + Number(failed)) / total, 0, 1) : null
)

/**
 * Defensive interpolation belt (AHA2-171 round 10): vue-i18n v8 returns a
 * missing key literally — with NO interpolation — until the CDN-loaded
 * translations pick up the new key from public/base_language.json. Until
 * then `{count}/{total} slides imported` renders as that raw literal string
 * instead of e.g. "8/8 slides imported". This generalizes the single-token
 * `interpolateCount` helper in TeamMembersBulkActionBar.vue /
 * TeamManagementEmbedIframe.vue to every `{name}` token present in `args`, so
 * it covers all of this feature's copy (count, total, n), not just count.
 * No-op once the key resolves to a real translation — `t()` already
 * substituted correctly, so the regex finds nothing left to replace.
 */
export const interpolate = (str, args) => {
  if (!str || !args) return str
  return Object.keys(args).reduce(
    (acc, name) => acc.replace(new RegExp(`\\{${name}\\}`, 'g'), String(args[name])),
    str,
  )
}

/**
 * Resolve the single UI state to render, given the raw signals. Terminal and
 * problem states win over the in-flight ones, in this priority order:
 *   complete > canceled > error > connection_lost > almost_done > converting > uploading
 *
 * @param {Object} s
 * @param {boolean} s.complete       import finished successfully
 * @param {boolean} s.canceled       user canceled (already-created slides kept)
 * @param {boolean} s.failed         import failed / timed out (AHA2-171 r20)
 * @param {boolean} s.connectionLost network is down mid-import
 * @param {boolean} s.isUploading    the file POST is still in flight
 * @param {string}  s.phase          backend job phase (see BACKEND_PHASE)
 * @param {number}  s.n              slides created so far
 * @param {number}  s.total          total slides expected (0/undefined = unknown)
 * @param {number}  s.captionsDone   captions completed so far (AHA2-171 r16)
 * @param {number}  s.captionsFailed captions permanently failed (counts as settled)
 * @param {number}  s.captionsTotal  total captions expected (0/undefined = unknown)
 * @returns {string} one of IMPORT_UI_STATE
 */
export const resolveImportUiState = ({
  complete = false,
  canceled = false,
  failed = false,
  connectionLost = false,
  isUploading = false,
  phase = null,
  n = 0,
  total = 0,
  captionsDone = 0,
  captionsFailed = 0,
  captionsTotal = 0,
} = {}) => {
  if (complete) return IMPORT_UI_STATE.COMPLETE
  if (canceled) return IMPORT_UI_STATE.CANCELED
  // A hard failure/timeout outranks connection-lost: once the job is genuinely
  // failed there is nothing to reconnect to.
  if (failed) return IMPORT_UI_STATE.ERROR
  if (connectionLost) return IMPORT_UI_STATE.CONNECTION_LOST

  // AHA2-171 r16: once captions.total is known it is the one real, bounded,
  // monotonic signal the backend emits — drive the state off it directly. The
  // bulk of captioning still reads as "Importing slides…" (CONVERTING); only
  // the tail (>= ALMOST_DONE_FRACTION) reads as "Almost done…". This replaces
  // the old "phase===captioning always -> almost done" shortcut, which jumped
  // straight to the 90% band the instant captioning started even at 0/44.
  const captionFraction = computeCaptionFraction(captionsDone, captionsFailed, captionsTotal)
  if (captionFraction != null) {
    return captionFraction >= ALMOST_DONE_FRACTION ? IMPORT_UI_STATE.ALMOST_DONE : IMPORT_UI_STATE.CONVERTING
  }

  // No caption signal yet: the indeterminate lead-in, unchanged (upload /
  // convert / importing, before captions.total exists).
  const fraction = total > 0 ? n / total : 0
  if (phase === BACKEND_PHASE.IMPORTING) {
    if (total > 0 && fraction >= ALMOST_DONE_FRACTION) return IMPORT_UI_STATE.ALMOST_DONE
    return IMPORT_UI_STATE.CONVERTING
  }
  // Captioning has started but the backend hasn't populated captions.total
  // yet (a brief transitional tick) — still reads as "Importing slides…"
  // rather than snapping back to "Uploading file…".
  if (phase === BACKEND_PHASE.CAPTIONING) return IMPORT_UI_STATE.CONVERTING

  // No importing phase yet: the file is uploading or the backend is still
  // converting it to page images. Both read as "Uploading file…".
  if (isUploading || phase === BACKEND_PHASE.CONVERTING || phase == null) {
    return IMPORT_UI_STATE.UPLOADING
  }
  return IMPORT_UI_STATE.UPLOADING
}

/**
 * The status label copy for a state (the sub line under the filename), as a
 * { key, args } pair. Verbatim from §7.
 *
 * AHA2-171 r18: r17's captioning-aware "Adding descriptions…" swap is
 * REMOVED here — the reporter found "Adding descriptions (36 of 44)" itself
 * confusing to end users and asked for the numeric readout to come from the
 * captioning phase directly instead (see countTextForState below). The
 * status line now stays the plain per-state label through the whole
 * convert/import/caption span: "Importing slides…" for CONVERTING, "Almost
 * done…" for ALMOST_DONE — never "Adding descriptions".
 */
export const statusTextForState = (state) => {
  switch (state) {
    case IMPORT_UI_STATE.UPLOADING:
      return { key: 'Uploading file…' }
    case IMPORT_UI_STATE.CONVERTING:
      return { key: 'Importing slides…' }
    case IMPORT_UI_STATE.ALMOST_DONE:
      return { key: 'Almost done…' }
    case IMPORT_UI_STATE.COMPLETE:
      return { key: 'Import complete' }
    case IMPORT_UI_STATE.CONNECTION_LOST:
      return { key: 'Waiting to reconnect…' }
    case IMPORT_UI_STATE.ERROR:
      return { key: 'Couldn’t import your file.' }
    default:
      return { key: '' }
  }
}

/**
 * Defensive total fallback (AHA2-171 round 11): the backend job's `result`
 * doesn't always carry a `slides.total` — during the plain "importing" phase
 * there is no total at all yet, and even once captioning starts the total
 * only lives in `captions.total` (see importProgress `total` computed / on
 * ImportProgressNotification.vue, which resolves `slides.total ??
 * captions.total`). For terminal/summary copy we never want a stale/missing
 * total to render as "0" next to a real created count — falling back to the
 * created count itself keeps the numerator and denominator in sync instead of
 * lying with a "0/0" or "44/0".
 *
 * AHA2-171 r20 (finding K): the DISPLAY total can also never be LESS than the
 * created count — that would read as "44/30". The assumption elsewhere is that
 * captions.total is the real slide total (verified from a real payload), so
 * this only bites if the backend ever captions a strict subset; clamp
 * defensively here (display copy only — the state/percent resolvers use the raw
 * total, so the pre-caption progress fraction is untouched).
 */
export const resolveDisplayTotal = (total, n) => {
  const created = Number.isFinite(Number(n)) ? Number(n) : 0
  return total > 0 ? Math.max(total, created) : created
}

/**
 * The count line copy for a state (§7). Null when the state shows no count,
 * so the card falls back to the phase label.
 *
 * AHA2-171 r18: the reporter found r17's "Adding descriptions (36 of 44)"
 * confusing and asked for two simpler rules instead:
 *   1. Pre-captioning (captions.total not yet known — uploading, converting,
 *      importing): NO numeric count line at all. slides.created/total was
 *      never a reliable signal here anyway (r11's "40/0 slides"), and
 *      showing a number before there's a real total to climb toward just
 *      confused people.
 *   2. Captioning (captions.total > 0): the readout is the exact SAME
 *      "{count}/{total} slides imported" copy COMPLETE already uses, with
 *      count = captions.done + captions.failed (the settled numerator that
 *      also drives the r16 bar) and total = captions.total. So it reads
 *      "36/44 slides imported" climbing to "44/44 slides imported", flowing
 *      continuously into the COMPLETE state below (same key, count==total).
 */
export const countTextForState = (state, {
  n = 0, total = 0, captionsDone = 0, captionsFailed = 0, captionsTotal = 0,
} = {}) => {
  const captionFraction = computeCaptionFraction(captionsDone, captionsFailed, captionsTotal)
  switch (state) {
    case IMPORT_UI_STATE.CONVERTING:
    case IMPORT_UI_STATE.ALMOST_DONE:
      if (captionFraction == null) return null
      return {
        key: '{count}/{total} slides imported',
        // AHA2-171 r20 (finding H): clamp the settled numerator to the total —
        // a backend that over-reports done+failed would otherwise render
        // "45/44" while the r16 bar (computeCaptionFraction) already clamps to
        // ≤100%. Keep the text and the bar in agreement.
        args: {
          count: Math.min(Number(captionsDone) + Number(captionsFailed), Number(captionsTotal)),
          total: captionsTotal,
        },
      }
    case IMPORT_UI_STATE.COMPLETE: {
      // Report the CREATED count regardless of whether a total ever arrived —
      // never let "0/0 slides imported" render when slides genuinely exist.
      const resolvedTotal = resolveDisplayTotal(total, n)
      return { key: '{count}/{total} slides imported', args: { count: n, total: resolvedTotal } }
    }
    case IMPORT_UI_STATE.CANCELED: {
      if (n <= 0) return { key: 'No slides were imported' }
      const resolvedTotal = resolveDisplayTotal(total, n)
      return { key: '{n}/{total} slides are now in your deck', args: { n, total: resolvedTotal } }
    }
    default:
      return null
  }
}

/**
 * The one-line status used in the import button's hover tooltip (§6):
 * "{filename} — {status} {n}/{total} slides". Returns '' when nothing active.
 */
export const tooltipStatusText = (state, {
  filename = '', n = 0, total = 0,
}, t) => {
  const translate = typeof t === 'function' ? t : (k) => k
  const statusMap = {
    [IMPORT_UI_STATE.UPLOADING]: translate('Uploading file…'),
    [IMPORT_UI_STATE.CONVERTING]: translate('Importing slides…'),
    [IMPORT_UI_STATE.ALMOST_DONE]: translate('Almost done…'),
    [IMPORT_UI_STATE.CONNECTION_LOST]: translate('Connection lost'),
  }
  const status = statusMap[state]
  if (!status) return ''
  const count = total > 0 && (state === IMPORT_UI_STATE.CONVERTING || state === IMPORT_UI_STATE.ALMOST_DONE)
    ? ` ${interpolate(translate('{n}/{total} slides', { n, total }), { n, total })}`
    : ''
  const name = filename ? `${filename} — ` : ''
  return `${name}${status}${count}`
}

/**
 * Whether the Cancel action should ask for confirmation. Per §4 this is driven
 * ENTIRELY by whether any slide has been created yet — not by the named state.
 * n === 0 → cancel immediately; n > 0 → inline confirmation.
 */
export const cancelNeedsConfirmation = (n) => Number(n) > 0

/**
 * Whether the current state is an "active import" for the purpose of the
 * trigger button spinner (§6): Uploading / Converting / Almost done — including
 * while the cancel confirmation is open, since the import is still running.
 */
export const isActiveImportState = (state) => (
  state === IMPORT_UI_STATE.UPLOADING
  || state === IMPORT_UI_STATE.CONVERTING
  || state === IMPORT_UI_STATE.ALMOST_DONE
)
